import { useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bell, CheckCircle, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'

interface PushNotificationsPromptModalProps {
  isOpen: boolean
  isSupported: boolean
  permission: NotificationPermission
  isProcessing: boolean
  error?: string | null
  onClose: () => void
  onEnable: () => void
  onTest: () => void
}

export function PushNotificationsPromptModal({
  isOpen,
  isSupported,
  permission,
  isProcessing,
  error,
  onClose,
  onEnable,
  onTest,
}: PushNotificationsPromptModalProps) {
  const { t } = useTranslation()

  const permissionLabel = useMemo(() => {
    if (!isSupported) {
      return t('settings.pushPromptUnsupported')
    }
    if (permission === 'granted') {
      return t('settings.pushPromptGranted')
    }
    if (permission === 'denied') {
      return t('settings.pushPromptDenied')
    }
    return t('settings.pushPromptDefault')
  }, [isSupported, permission, t])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Bell className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    {t('settings.pushPromptTitle')}
                  </h2>
                  <p className="text-sm text-text-secondary">{t('settings.pushPromptDescription')}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-sm text-text-secondary">
              {permissionLabel}
            </div>

            {error && error !== permissionLabel && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
            )}

            {permission === 'granted' && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                <CheckCircle className="h-4 w-4" />
                {t('settings.pushPromptReady')}
              </div>
            )}

            <div className="mt-6 flex flex-col gap-3">
              <Button
                onClick={onEnable}
                disabled={!isSupported || isProcessing}
                loading={isProcessing}
              >
                {t('settings.pushPromptEnable')}
              </Button>
              <Button
                variant="outline"
                onClick={onTest}
                disabled={!isSupported || permission !== 'granted' || isProcessing}
              >
                {t('settings.pushPromptTest')}
              </Button>
              <Button variant="ghost" onClick={onClose} disabled={isProcessing}>
                {t('settings.pushPromptLater')}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
